import { Payments, SplitPaymentsGroups } from '@prisma/client';
import { AxiosError } from 'axios';
import API_URL from '../apiconfig';
import {
  CheckoutData,
  CreateSplitPaymentPayload,
  CreateSplitPaymentResponse,
  CreateSplitPaymentsGroupPayload,
  FullPayment,
  InitiatePaymentPayload,
  InitiatePaymentResponse,
  PaymentsGroup,
  PaymentsGroupResponse,
  RefundPaymentPayload,
  RefundPaymentResponse,
  SortableKeys,
  SplitPaymentsGroupWithCustomer,
  SplitPaymentsGroupWithPayments,
} from '../types/payments';
import axios from './api';
import { CheckoutFormValues } from '../types/appointments';
import { updateCustomer } from './user';
import { getCustomerUpdateError } from './appointments';
import { SortValues } from '../types/ui';
import client from '../utils/react-query/client';

export async function refundPayment(
  paymentId: string,
  payload: RefundPaymentPayload,
) {
  try {
    const paymentResult = await axios.patch(
      `${API_URL}/v1/payments/${paymentId}/refund`,
      payload,
    );

    return paymentResult.data.result as RefundPaymentResponse;
  } catch (error) {
    throw new Error('Failed to refund payment');
  }
}

export async function sendPaymentReceipt(paymentId: string) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/${paymentId}/receipt`,
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    if ((error as AxiosError).response?.status === 429) {
      throw new Error('Please wait before sending another receipt');
    }
    throw new Error('Failed to send payment receipt');
  }
}

export async function initiatePayment(
  payload: InitiatePaymentPayload,
  customer?: CheckoutFormValues['customer'],
) {
  let customerId: string | undefined =
    customer?.customerId || payload.customerId;
  try {
    if (customer?.customerId) {
      await updateCustomer(
        {
          firstName: customer.firstName,
          lastName: customer.lastName,
          phoneNumber: customer.phoneNumber,
          email: customer.email,
          dob: customer.dob,
        },
        customer.customerId,
      ).catch(err => {
        throw new Error(getCustomerUpdateError(err));
      });
    } else if (customer) {
      const phoneNumber =
        customer.phoneNumber?.charAt(0) === '+'
          ? customer.phoneNumber
          : `+${customer.phoneNumber}`;

      const createCustomerPayload: any = {
        firstName: customer.firstName,
        lastName: customer.lastName,
        phoneNumber,
      };

      if (customer.email) {
        createCustomerPayload.email = customer.email;
      }

      if (customer.dob) {
        createCustomerPayload.dob = customer.dob;
      }

      const createCustomerResponse = await axios
        .post(`${API_URL}/v1/customers`, createCustomerPayload)
        .catch(error => {
          throw new Error(error.response.data.message);
        });

      customerId = createCustomerResponse.data.result.customerId;
    }

    const paymentResult = await axios.post(`${API_URL}/v1/payments`, {
      ...payload,
      customerId: customerId || payload.customerId,
    });

    return paymentResult.data.result as InitiatePaymentResponse;
  } catch (error) {
    throw new Error('Failed to initiate payment');
  }
}

export async function getCheckoutData(payload: InitiatePaymentPayload) {
  try {
    const paymentResult = await axios.post(`${API_URL}/v1/payments/calculate`, {
      ...payload,
      isEstimateOnly: true,
    });

    return paymentResult.data.result as CheckoutData;
  } catch (error) {
    throw new Error('Failed to get checkout data');
  }
}

export async function cancelCheckout({
  appointmentId,
  orderId,
  terminalReaderId,
}: {
  appointmentId?: string;
  orderId?: string;
  terminalReaderId?: string;
}) {
  try {
    const paymentResult = await axios.patch(
      `${API_URL}/v1/payments/cancel-checkout`,
      {
        appointmentId,
        orderId,
        terminalReaderId,
      },
    );

    return paymentResult.data.result as {
      appointmentId: string | undefined;
      orderId: string | undefined;
    };
  } catch (error) {
    throw new Error('Failed to cancel checkout');
  }
}

export async function getGroupedPayments({
  businessId,
  page = 1,
  sort,
}: {
  businessId: string;
  page?: number;
  sort?: SortValues<SortableKeys>;
}) {
  try {
    const paymentResult = await axios.get(`${API_URL}/v1/payments`, {
      params: {
        businessId,
        page,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
      },
    });

    return paymentResult.data.result as PaymentsGroupResponse;
  } catch (error) {
    throw new Error('Failed to get grouped payments');
  }
}

export async function getPaymentsGroup({
  groupPaymentsId,
}: {
  groupPaymentsId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/group/${groupPaymentsId}`,
    );

    return paymentResult.data.result as PaymentsGroup;
  } catch (error) {
    throw new Error('Failed to get payments group');
  }
}

export async function getPayment({
  paymentId,
  signal,
}: {
  paymentId: string;
  signal?: AbortSignal;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/${paymentId}`,
      { signal },
    );

    return paymentResult.data.result as FullPayment;
  } catch (error) {
    throw new Error('Failed to get payment');
  }
}

export async function getPaymentPaymentsGroup({
  paymentId,
}: {
  paymentId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/${paymentId}/group`,
    );

    return paymentResult.data.result as PaymentsGroup;
  } catch (error) {
    throw new Error('Failed to get payment payments group');
  }
}

export async function voidPayment({ paymentId }: { paymentId: string }) {
  try {
    const paymentResult = await axios.delete(
      `${API_URL}/v1/payments/${paymentId}`,
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    throw new Error('Failed to void payment');
  }
}

export async function getSplitPaymentsGroup({
  splitPaymentsGroupId,
}: {
  splitPaymentsGroupId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}`,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithPayments;
  } catch (error) {
    throw new Error('Failed to get split payments group');
  }
}
export async function createSplitPaymentsGroup(
  payload: CreateSplitPaymentsGroupPayload,
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split`,
      payload,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithCustomer;
  } catch (error) {
    throw new Error('Failed to create split payments group');
  }
}

export async function createSplitPayment(
  splitPaymentsGroupId: string,
  payload: CreateSplitPaymentPayload,
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/payment`,
      payload,
    );

    return paymentResult.data.result as CreateSplitPaymentResponse;
  } catch (error) {
    throw new Error('Failed to create split payment');
  }
}

export async function resetSplitPaymentsGroup(splitPaymentsGroupId: string) {
  try {
    const paymentResult = await axios.patch(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/reset`,
    );

    return paymentResult.data.result as SplitPaymentsGroups;
  } catch (error) {
    throw new Error('Failed to reset split payments group');
  }
}

export async function finalizeSplitPayments(splitPaymentsGroupId: string) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/charge`,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithPayments;
  } catch (error) {
    throw new Error('Failed to finalize split payments');
  }
}

export async function processTerminalPayment(paymentId: string) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/${paymentId}/terminal`,
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    throw new Error('Failed to process terminal payment');
  }
}

export async function invalidateGroupedPayments() {
  await client.invalidateQueries({
    queryKey: ['grouped-payments'],
    exact: false,
  });
}

export async function refetchGroupedPayments() {
  await client.refetchQueries({
    queryKey: ['grouped-payments'],
    exact: false,
    type: 'active',
  });
}

export async function invalidatePaymentPaymentsGroup(paymentId: string) {
  await client.invalidateQueries({
    queryKey: ['payment-payments-group', paymentId],
    exact: false,
  });
}

export async function invalidatePaymentPaymentsGroups() {
  await client.invalidateQueries({
    queryKey: ['payment-payments-group'],
    exact: false,
  });
}

export async function invalidateCreateSplitPaymentsGroup() {
  await client.invalidateQueries({
    queryKey: ['create-split-payments-group'],
    exact: false,
  });
}
